/**
 * Pinned runnables in the Preview tab. A compose file or Dockerfile pinned in a service (utils/runnablesModel) runs as
 * a preview-runner session of its own (`services/app--compose-docker-compose-yaml`); the Preview lists it under its
 * owner ("app · docker-compose.yaml") in the header's service picker and as a preview tab of its own, opens it from
 * Apps' "Open preview" (`collab:openPreview` with its runner name), shows its session's `previewUrl` (projectRun
 * appUrlOf), and Retry / Start restart that session with its runnable payload (never the owner's build). When the
 * owner's own session does not serve its app while its pinned stack runs (projectRun foldPinned: "carried"), the
 * Preview of the owner defaults to the stack.
 */
import { appUrlOf, ownerOfSession, projectRunItems } from '../components/sandbox/projectRun';
import { isRunning, sessionIdFor } from '../services/SandboxService';
import { joinRoute } from './collab3Model';
import { serviceEntryFor } from './previewReadiness';
import { servicePath } from './projectOverview';
import { parseRunnableName } from './runnablesModel';

const shortOf = (name) => String(name || '').replace(/^services\//, '');

/** "app · docker-compose.yaml" (the kind when the file is not known). */
export const runnableLabel = (owner, file, kind) => `${shortOf(owner)} · ${file || (kind === 'compose' ? 'Compose' : 'Dockerfile')}`;

/** The runner name for a requested preview name (`app--compose-…`, `services/app--compose-…`), or null when not a runnable's. */
export function runnableNameOf(name) {
  const text = String(name || '').trim().replace(/\/+$/, '');
  if (!text) { return null; }
  const full = servicePath(text);
  return parseRunnableName(full) ? full : null;
}

const entryOfPinned = (item) => ({
  serviceName: item.serviceName,
  owner: item.service,
  label: runnableLabel(item.service, item.file, item.kind),
  runnable: { service: item.service, kind: item.kind, file: item.file, ...(item.entry ? { entry: item.entry } : {}) },
});

/**
 * The Preview's pinned runnable entries, each `{ serviceName, owner, label, runnable, service }` (`runnable`
 * `{ service, kind, file, entry? }`, what Apps' Start sends, null when the file is not known; `service` the runner's
 * entry or null): the pinned files of the scan (`pinned`, runnablesModel pinnedItems), then the ones projectRunItems
 * knows besides (sessions the runner has for the project's services, the default app when it is a pinned file).
 * `owners`: the project's own service names.
 */
export function pinnedPreviewEntries({ pinned = [], owners = [], runnerServices = [], defaultRunnable = null } = {}) {
  const byName = new Map();
  (pinned || []).forEach((item) => { if (item?.serviceName) { byName.set(item.serviceName, entryOfPinned(item)); } });
  const ownerItems = (owners || []).map((name) => servicePath(name)).filter((name) => name !== '.')
    .map((serviceName) => ({ serviceName, service: serviceEntryFor(runnerServices, serviceName) }));
  projectRunItems(ownerItems, runnerServices, defaultRunnable).forEach((item) => {
    const parsed = parseRunnableName(item.serviceName);
    if (!parsed || byName.has(item.serviceName)) { return; }
    byName.set(item.serviceName, item.runnable?.file
      ? entryOfPinned({ ...item.runnable, serviceName: item.serviceName })
      : { serviceName: item.serviceName, owner: parsed.service, label: runnableLabel(parsed.service, null, parsed.kind), runnable: null });
  });
  return [...byName.values()].map((entry) => ({ ...entry, service: serviceEntryFor(runnerServices, entry.serviceName) }));
}

/**
 * The entry a requested preview name means when it is a pinned runnable's (else null): the known one, or one built
 * from its runner name alone (its file not known yet) so the Preview never falls back to the owner.
 */
export function resolveRunnablePreview(name, entries = [], runnerServices = null) {
  const wanted = runnableNameOf(name);
  if (!wanted) { return null; }
  const parsed = parseRunnableName(wanted);
  const known = (entries || []).find((entry) => entry.serviceName === wanted)
    || { serviceName: wanted, owner: parsed.service, label: runnableLabel(parsed.service, null, parsed.kind), runnable: null, service: null };
  return runnerServices ? { ...known, service: serviceEntryFor(runnerServices, wanted) } : known;
}

const failed = (service) => Boolean(service) && (['failed', 'error'].includes(service.buildStatus) || (service.crashed && service.buildStatus !== 'building'));

/** Why a runnable session is not up, in one line. */
export function runnableProblem(service) {
  const crash = (Array.isArray(service?.crashOutput) ? service.crashOutput : []).map((line) => String(line || '').trim()).filter(Boolean);
  const reason = String(service?.buildError || '').split('\n')[0].replace(/^Build failed:\s*/i, '').trim() || crash[crash.length - 1] || '';
  return `The stack failed to start${reason ? `: ${reason}` : '. Read the log to see why.'}`;
}

/**
 * The Preview tab of a pinned runnable from its runner state: its app (appUrlOf, at `route`) when it runs, following
 * a build, its problem when it failed, else stopped (Start starts it). `external` keeps it when the configuration's
 * tabs are reset; `runnable`/`owner` tell the Preview how to start it.
 */
export function runnablePreviewTab(projectId, entry, { route = '/', now = Date.now() } = {}) {
  const service = entry.service || null;
  const base = failed(service) ? null : appUrlOf(projectId, { serviceName: entry.serviceName, service });
  const common = {
    serviceName: entry.serviceName,
    displayName: entry.label,
    owner: entry.owner,
    runnable: entry.runnable || null,
    sessionId: service?.sessionId || sessionIdFor(projectId, entry.serviceName),
    framework: 'runnable',
    containerPort: null,
    external: true,
    iframeKey: now,
    isEmptyService: false,
  };
  if (base) {
    return { ...common, url: joinRoute(base, route) || base, baseUrl: base, loading: false, iframeLoaded: false, error: null, stopped: false, starting: false };
  }
  if (service && service.buildStatus === 'building' && !service.crashed) {
    return { ...common, url: null, loading: true, followingBuild: true, renderPhase: 'starting', error: null, stopped: false, starting: false };
  }
  if (failed(service)) {
    return { ...common, url: null, loading: false, error: runnableProblem(service), stopped: false, starting: false };
  }
  return { ...common, url: null, loading: false, error: null, stopped: true, starting: false };
}

/** `tabs` with `tab` in (replacing the one of the same service) and its index. */
export function withPreviewTab(tabs, tab) {
  const list = Array.isArray(tabs) ? tabs : [];
  const index = list.findIndex((item) => item.serviceName === tab.serviceName);
  if (index < 0) { return { tabs: [...list, tab], index: list.length }; }
  const next = [...list];
  next[index] = { ...list[index], ...tab };
  return { tabs: next, index };
}

/**
 * The pinned stack the Preview of `ownerName` shows instead of the owner (an entry, or null): the owner's own session
 * does not serve its app while one of its pinned files runs (projectRunItems folds the owner away), the default app's
 * file first. `defaultRunnable` utils/defaultApp's.
 */
export function stackCarrying(projectId, ownerName, { entries = [], runnerServices = [], defaultRunnable = null } = {}) {
  if (!ownerName || runnableNameOf(ownerName)) { return null; }
  const owner = servicePath(ownerName);
  const items = projectRunItems([{ serviceName: owner, service: serviceEntryFor(runnerServices, owner) }], runnerServices, defaultRunnable);
  if (items.some((item) => item.serviceName === owner)) { return null; }
  const carriers = items.filter((item) => ownerOfSession(item.serviceName) === owner && isRunning(item.service) && appUrlOf(projectId, item));
  if (!carriers.length) { return null; }
  const chosen = carriers.find((item) => item.runnable) || carriers[0];
  return resolveRunnablePreview(chosen.serviceName, entries, runnerServices);
}

/** The payload a start of the runnable sends (Apps' Start): `{ serviceName, service, runnable }` for startServices. */
export function runnableStartItem(tab, runnerServices = []) {
  if (!tab?.runnable?.file) { return null; }
  return { serviceName: tab.serviceName, service: serviceEntryFor(runnerServices, tab.serviceName), runnable: tab.runnable };
}

/** A Preview tab of a pinned runnable (runnablePreviewTab's). */
export const isRunnableTab = (tab) => tab?.framework === 'runnable';

/**
 * The header's service picker items with the pinned runnables (`entries`) each right under its owner's item
 * (`{ value, icon, title, desc, key, status, runnable: true }`, `status(serviceName)` its dot); a runnable whose
 * owner is not listed comes last.
 */
export function withRunnableItems(items, entries, status = () => undefined) {
  const runnableItem = (entry) => ({
    value: entry.serviceName,
    icon: 'package',
    title: entry.label,
    desc: entry.runnable?.kind === 'dockerfile' ? 'Pinned Dockerfile, its own preview' : 'Pinned Compose file, its own preview',
    key: entry.serviceName,
    status: status(entry.serviceName) || undefined,
    runnable: true,
  });
  const list = entries || [];
  const placed = new Set();
  const out = (items || []).flatMap((item) => {
    const children = list.filter((entry) => servicePath(entry.owner) === servicePath(item.value));
    children.forEach((entry) => placed.add(entry.serviceName));
    return [item, ...children.map(runnableItem)];
  });
  return [...out, ...list.filter((entry) => !placed.has(entry.serviceName)).map(runnableItem)];
}
